import * as React from 'react';
import { Link } from 'react-router-dom';
import { MUIDataTableColumnDef } from 'mui-datatables';
import { formatMonitoringDeliveryDate, MonitoringPage } from './MonitoringPage';

export interface IOrderTotalMonitoringRow {
  orderId: number;
  orderNumber: string;
  parentOrderId?: number;
  status: string;
  lifetimeStatus: string;
  deliveryDate?: string | null;
  cachedTotal: number;
  legacySubTotal: number;
  legacyGst: number;
  legacyFuelLevy: number;
  legacyTotal: number;
  calculatorSubTotal: number;
  calculatorGst: number;
  calculatorFuelLevy: number;
  calculatorTotal: number;
  legacyFamilySubTotal?: number;
  legacyFamilyGst?: number;
  legacyFamilyFuelLevy?: number;
  legacyFamilyTotal?: number;
  calculatorFamilySubTotal?: number;
  calculatorFamilyGst?: number;
  calculatorFamilyFuelLevy?: number;
  calculatorFamilyTotal?: number;
  cachedTotalMatches: boolean;
  orderCostsMatch: boolean;
  familyCostsMatch?: boolean;
  costsMatch: boolean;
}

const money = (value?: number) => (value === undefined || value === null ? 'N/A' : value.toFixed(2));
const match = (value?: boolean) => (value === undefined || value === null ? 'N/A' : value ? 'Yes' : 'No');
const optionalNumber = (value?: number) => (value === undefined || value === null ? 'N/A' : value.toString());

const hiddenColumnOptions = {
  customBodyRender: money,
  display: 'false' as 'false',
  filter: false,
  sort: false,
};

const groupedColumnOptions = {
  download: false,
  filter: false,
  sort: false,
};

interface IOrderTotalsTableMeta {
  rowData: unknown;
}

interface ICostField {
  label: string;
  name: keyof IOrderTotalMonitoringRow;
}

const getRowValue = (
  tableMeta: IOrderTotalsTableMeta,
  name: keyof IOrderTotalMonitoringRow,
): number | undefined => {
  if (!Array.isArray(tableMeta.rowData)) return undefined;

  const index = orderTotalsColumns.findIndex((column) => typeof column !== 'string' && column.name === name);
  return index >= 0 ? tableMeta.rowData[index] as number | undefined : undefined;
};

const renderCostRow = (label: string, value?: number) => (
  <div className="monitoring-values-row" key={label}>
    <span className="monitoring-values-label">{label}</span>
    <span>{money(value)}</span>
  </div>
);

const renderCostSection = (
  title: string,
  tableMeta: IOrderTotalsTableMeta,
  fields: ICostField[],
) => (
  <div className="monitoring-values-section">
    <strong>{title}</strong>
    {fields.map((field) => renderCostRow(field.label, getRowValue(tableMeta, field.name)))}
  </div>
);

const renderCosts = (
  tableMeta: IOrderTotalsTableMeta,
  orderFields: ICostField[],
  familyFields: ICostField[],
) => (
  <div className="monitoring-values">
    {renderCostSection('Order', tableMeta, orderFields)}
    {renderCostSection('Family', tableMeta, familyFields)}
  </div>
);

const renderLegacyCosts = (_value: unknown, tableMeta: IOrderTotalsTableMeta) => renderCosts(
  tableMeta,
  [
    { label: 'Subtotal', name: 'legacySubTotal' },
    { label: 'GST', name: 'legacyGst' },
    { label: 'Fuel Levy', name: 'legacyFuelLevy' },
    { label: 'Total', name: 'legacyTotal' },
  ],
  [
    { label: 'Subtotal', name: 'legacyFamilySubTotal' },
    { label: 'GST', name: 'legacyFamilyGst' },
    { label: 'Fuel Levy', name: 'legacyFamilyFuelLevy' },
    { label: 'Total', name: 'legacyFamilyTotal' },
  ],
);

const renderCalculatorCosts = (_value: unknown, tableMeta: IOrderTotalsTableMeta) => renderCosts(
  tableMeta,
  [
    { label: 'Subtotal', name: 'calculatorSubTotal' },
    { label: 'GST', name: 'calculatorGst' },
    { label: 'Fuel Levy', name: 'calculatorFuelLevy' },
    { label: 'Total', name: 'calculatorTotal' },
  ],
  [
    { label: 'Subtotal', name: 'calculatorFamilySubTotal' },
    { label: 'GST', name: 'calculatorFamilyGst' },
    { label: 'Fuel Levy', name: 'calculatorFamilyFuelLevy' },
    { label: 'Total', name: 'calculatorFamilyTotal' },
  ],
);

export const orderTotalsColumns: MUIDataTableColumnDef[] = [
  {
    name: 'orderNumber',
    label: 'Order',
    options: {
      customBodyRender: (value: string) => (
        <Link to={`/view-order/${value}`} target="_blank">
          {value}
        </Link>
      ),
    },
  },
  { name: 'deliveryDate', label: 'Delivery Date', options: { customBodyRender: formatMonitoringDeliveryDate } },
  { name: 'parentOrderId', label: 'Parent Order', options: { customBodyRender: optionalNumber } },
  { name: 'status', label: 'Status' },
  { name: 'lifetimeStatus', label: 'Lifetime Status' },
  { name: 'cachedTotal', label: 'Cached Total', options: { customBodyRender: money } },
  {
    name: 'legacyTotal',
    label: 'Legacy',
    options: { ...groupedColumnOptions, customBodyRender: renderLegacyCosts },
  },
  {
    name: 'calculatorTotal',
    label: 'Calculator',
    options: { ...groupedColumnOptions, customBodyRender: renderCalculatorCosts },
  },
  { name: 'legacySubTotal', label: 'Legacy Subtotal', options: hiddenColumnOptions },
  { name: 'legacyGst', label: 'Legacy GST', options: hiddenColumnOptions },
  { name: 'legacyFuelLevy', label: 'Legacy Fuel Levy', options: hiddenColumnOptions },
  { name: 'calculatorSubTotal', label: 'Calculator Subtotal', options: hiddenColumnOptions },
  { name: 'calculatorGst', label: 'Calculator GST', options: hiddenColumnOptions },
  { name: 'calculatorFuelLevy', label: 'Calculator Fuel Levy', options: hiddenColumnOptions },
  { name: 'legacyFamilySubTotal', label: 'Legacy Family Subtotal', options: hiddenColumnOptions },
  { name: 'legacyFamilyGst', label: 'Legacy Family GST', options: hiddenColumnOptions },
  { name: 'legacyFamilyFuelLevy', label: 'Legacy Family Fuel Levy', options: hiddenColumnOptions },
  { name: 'legacyFamilyTotal', label: 'Legacy Family Total', options: hiddenColumnOptions },
  { name: 'calculatorFamilySubTotal', label: 'Calculator Family Subtotal', options: hiddenColumnOptions },
  { name: 'calculatorFamilyGst', label: 'Calculator Family GST', options: hiddenColumnOptions },
  { name: 'calculatorFamilyFuelLevy', label: 'Calculator Family Fuel Levy', options: hiddenColumnOptions },
  { name: 'calculatorFamilyTotal', label: 'Calculator Family Total', options: hiddenColumnOptions },
  { name: 'cachedTotalMatches', label: 'Cached Total Matches', options: { customBodyRender: match } },
  { name: 'orderCostsMatch', label: 'Order Costs Match', options: { customBodyRender: match } },
  { name: 'familyCostsMatch', label: 'Family Costs Match', options: { customBodyRender: match } },
  { name: 'costsMatch', label: 'All Costs Match', options: { customBodyRender: match } },
];

export const OrderTotalsMonitor = () => (
  <MonitoringPage
    apiPath="/api/monitoring/order-totals"
    allowTba
    columns={orderTotalsColumns}
    emptyMessage="No orders found for the selected criteria."
    title="Order Totals Monitor"
  />
);
